<template>
    <div class="content">
        <base-header class="pb-6">
            <div class="row align-items-center py-4">
                <div class="col-lg-6 col-7">
                    <h6 class="h2 text-white d-inline-block mb-0">Distribution Rules</h6>
                    <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
                        <route-bread-crumb></route-bread-crumb>
                    </nav>
                </div>
            </div>
        </base-header>
        <div class="container-fluid mt--6">
            <div>
                <card class="no-border-card" body-classes="px-0 pb-1" footer-classes="pb-2">
                    <template slot="header">
                        <div class="row align-items-center">
                            <div class="col-9">
                                <h3 class="mb-0" id="leads_main_heading">Distribution Rules</h3>
                            </div>
                            <div class="col-3 text-right">
                                <el-switch v-model="distribute_leads_evenly"
                                    @change="changeDistributionStyle"></el-switch> <span class="text-sm">Distribute
                                    Leads Evenly</span>
                            </div>
                        </div>
                    </template>
                    <div>
                        <el-table :data="venue_specialists" row-key="id" v-loading="loading"
                            header-row-class-name="thead-light" id="lead_venue_specialists_table">
                            <el-table-column label="Name" prop="name" min-width="130px" sortable="custom">
                                <template v-slot="{ row }">
                                    <span class="font-weight-600 name mb-0 text-sm">{{
                                        row.name
                                        }}</span>
                                </template>
                            </el-table-column>

                            <el-table-column label="Wedding" min-width="70px" align="center" class-name="event-type-cell">
                                <template v-slot="{ row }">
                                    <base-checkbox v-model="row.event_type_wedding"
                                        @input="updateField(row.id, 'event_type_wedding', row.event_type_wedding)">
                                    </base-checkbox>
                                </template>
                            </el-table-column>
                            <el-table-column label="Corporate" min-width="70px" align="center" class-name="event-type-cell">
                                <template v-slot="{ row }">
                                    <base-checkbox v-model="row.event_type_corporate"
                                        @input="updateField(row.id, 'event_type_corporate', row.event_type_corporate)">
                                    </base-checkbox>
                                </template>
                            </el-table-column>
                            <el-table-column label="Social" min-width="70px" align="center" class-name="event-type-cell">
                                <template v-slot="{ row }">
                                    <base-checkbox v-model="row.event_type_social"
                                        @input="updateField(row.id, 'event_type_social', row.event_type_social)">
                                    </base-checkbox>
                                </template>
                            </el-table-column>

                            <el-table-column label="Industries" min-width="200px">
                                <template v-slot="{ row }">
                                    <el-select
                                        v-if="row.event_type_corporate"
                                        v-model="row.distribution_rule.industry"
                                        multiple
                                        collapse-tags
                                        placeholder="Select industries..."
                                        @change="updateField(row.id, 'industry', row.distribution_rule.industry)"
                                        size="mini"
                                        class="w-100"
                                    >
                                        <el-option
                                            v-for="industry in industries"
                                            :key="industry"
                                            :label="industry"
                                            :value="industry"
                                        >
                                        </el-option>
                                    </el-select>
                                </template>
                            </el-table-column>

                            <el-table-column label="Active Days" min-width="200px">
                                <template v-slot="{ row }">
                                    <el-popover
                                        placement="bottom-start"
                                        trigger="click"
                                        popper-class="active-days-popover"
                                    >
                                        <div class="active-days-group">
                                            <button
                                                v-for="day in weekDays"
                                                :key="day.value"
                                                type="button"
                                                class="btn btn-sm active-day-btn"
                                                :class="isDayActive(row, day.value) ? 'btn-primary' : 'btn-outline-secondary'"
                                                :title="day.label"
                                                @click.prevent="toggleActiveDay(row, day.value)"
                                            >
                                                {{ day.short }}
                                            </button>
                                        </div>
                                        <el-button
                                            slot="reference"
                                            size="mini"
                                            class="active-days-trigger"
                                        >
                                            {{ activeDaysLabel(row) }}
                                            <i class="el-icon-arrow-down el-icon--right"></i>
                                        </el-button>
                                    </el-popover>
                                </template>
                            </el-table-column>

                            <el-table-column label="Lead Cap (/MO)" min-width="140px" class-name="lead-cap-cell">
                                <template v-slot="{ row }">
                                    <span @click.prevent="updateField(row.id, 'max_cap_active', !row.max_cap_active)"
                                        id="active_toggle">
                                        <base-switch v-model="row.max_cap_active"></base-switch>
                                    </span>
                                    <el-input-number placeholder=" " v-model="row.max_cap"
                                        @change="updateField(row.id, 'max_cap', row.max_cap)"
                                        class="inline-input" size="mini" resize="horizontal"
                                        v-if="row.max_cap_active" :min="1"></el-input-number>
                                </template>
                            </el-table-column>

                            <el-table-column label="Paused" min-width="110px" class-name="paused-attributes">
                                <template v-slot="{ row }">
                                    <div v-if="row.active">
                                        <span @click.prevent="updatePausedAttribute(row)">
                                            <base-switch v-bind:value="isPaused(row)"></base-switch>
                                        </span>
                                        <el-button v-if="isPaused(row)"
                                            @click.stop="showPauseUntilDatePicker = true; selectedVenueRep = row"
                                            size="mini"
                                            class="paused-date-btn text-center" :class="{'text-danger': ! pausedUpto(row) }"
                                        >
                                            {{ pausedUpto(row) || 'Select a date' }}
                                        </el-button>
                                    </div>
                                </template>
                            </el-table-column>

                            <el-table-column label="Active" min-width="100px">
                                <template v-slot="{ row }">
                                    <span @click.prevent="updateField(row.id, 'active', !row.active)"
                                        id="toggle_scheduler_access">
                                        <base-switch v-model="row.active"></base-switch>
                                    </span>
                                </template>
                            </el-table-column>
                        </el-table>
                    </div>
                </card>
            </div>
        </div>
        <el-dialog v-if="showPauseUntilDatePicker"
            width="440px"
            :visible.sync="showPauseUntilDatePicker"
            :append-to-body="true" center
            :before-close="handlePauseModalClose"
        >
            <template #title>
                <div class="font-weight-bold">
                    Select A Date
                </div>
            </template>
            <div class="row">
                <select-round-robin-pause-until-date :venue-rep="selectedVenueRep"
                    :show-error="showDateSelectionError"
                    @update-pause-till-date="updatePausedTillAttribute($event)" />
            </div>

        </el-dialog>
    </div>
</template>

<script>
    import { Table, TableColumn, Select, Option, Loading, InputNumber, Input, Switch, Button } from "element-ui";
    import RouteBreadCrumb from "@/components/Breadcrumb/RouteBreadcrumb";
    import tableMixin from "@/helpers/remoteTableMixin";
    import { LeadService } from "@/services";
    import CompanyService from "@/services/company.service";
    import { mapState } from "vuex";
    import { dialog } from "element-ui";
    import SelectRoundRobinPauseUntilDate from "@/views/Widgets/Inspections/SelectRoundRobinPauseUntilDate.vue";

    export default {
        mixins: [tableMixin],
        components: {
            RouteBreadCrumb,
            SelectRoundRobinPauseUntilDate,
            [dialog.name]: dialog,
            [Select.name]: Select,
            [Option.name]: Option,
            [Table.name]: Table,
            [TableColumn.name]: TableColumn,
            [InputNumber.name]: InputNumber,
            [Switch.name]: Switch,
            [Button.name]: Button
        },
        data() {
            return {
                venue_specialists: [],
                distribute_leads_evenly: true,
                loading: true,
                showPauseUntilDatePicker: false,
                wasAPauseTillDateSelected: false,
                showDateSelectionError: false,
                selectedVenueRep: {},
                industries: [],
                weekDays: [
                    { value: 'Mon', short: 'M', label: 'Monday' },
                    { value: 'Tue', short: 'T', label: 'Tuesday' },
                    { value: 'Wed', short: 'W', label: 'Wednesday' },
                    { value: 'Thu', short: 'T', label: 'Thursday' },
                    { value: 'Fri', short: 'F', label: 'Friday' },
                    { value: 'Sat', short: 'S', label: 'Saturday' },
                    { value: 'Sun', short: 'S', label: 'Sunday' }
                ]
            };
        },
        created() {
            this.loadPage();
            this.loadIndustries();
        },
        computed: {
            ...mapState("country/", {
                country: (state) => state.country || "AU",
            }),
            isPaused: () => (row) => {
                return row.distribution_rule?.is_paused;
            },
            pausedUpto: () => row => row.distribution_rule?.paused_upto
        },
        methods: {
            isDayActive(row, day) {
                const days = row.distribution_rule?.active_days;
                return Array.isArray(days) && days.includes(day);
            },
            activeDaysLabel(row) {
                const days = row.distribution_rule?.active_days;
                if (! Array.isArray(days) || days.length === 0) {
                    return 'No days';
                }
                if (days.length === this.weekDays.length) {
                    return 'All days';
                }
                // Preserve canonical Mon-Sun ordering in the label
                return this.weekDays
                    .filter(d => days.includes(d.value))
                    .map(d => d.value)
                    .join(', ');
            },
            async toggleActiveDay(row, day) {
                const current = Array.isArray(row.distribution_rule?.active_days)
                    ? [...row.distribution_rule.active_days]
                    : [];
                const idx = current.indexOf(day);
                if (idx === -1) {
                    current.push(day);
                } else {
                    current.splice(idx, 1);
                }
                // Preserve canonical Mon-Sun ordering
                const ordered = this.weekDays
                    .map(d => d.value)
                    .filter(v => current.includes(v));
                await this.updateField(row.id, 'active_days', ordered);
            },
            handlePauseModalClose(done) {
                if (this.pausedUpto(this.selectedVenueRep) || this.wasAPauseTillDateSelected) {
                    this.showDateSelectionError = false;
                    this.selectedVenueRep = {};
                    done();
                } else {
                    this.showDateSelectionError = true;
                }
            },
            async updatePausedAttribute(row) {
                this.selectedVenueRep = row;
                this.showPauseUntilDatePicker = row.distribution_rule?.is_paused ? false : true;
                await this.updateField(row.id, 'pause', !row.distribution_rule?.is_paused);
            },
            async updatePausedTillAttribute(payload) {
                if (payload.date) {
                    this.wasAPauseTillDateSelected = true;
                }

                await this.updateField(payload.id, 'paused_till', payload.date);
                this.showPauseUntilDatePicker = false;
                this.selectedVenueRep = {};
                this.wasAPauseTillDateSelected = false;
                this.showDateSelectionError = false;
            },
            async loadPage() {
                this.loading = true;
                let data = await LeadService.getVenueSpecialists();
                let temp = [];
                data.data.forEach(vc => {
                    if (vc.country === this.country) {
                        // Initialize distribution_rule object if not present
                        if (! vc.distribution_rule) {
                            vc.distribution_rule = {};
                        }
                        // Initialize industry array if not present
                        if (!vc.distribution_rule.industry) {
                            vc.distribution_rule.industry = [];
                        }
                        // Initialize active_days to all days if not set (no restriction by default)
                        if (! Array.isArray(vc.distribution_rule.active_days)
                            || vc.distribution_rule.active_days.length === 0) {
                            vc.distribution_rule.active_days = this.weekDays.map(d => d.value);
                        }
                        temp.push(vc);
                    }
                });
                temp.sort(function (a, b) {
                    a = a.name;
                    b = b.name;
                    return (a === b) ? 0 : (a > b) ? 1 : -1;
                });
                this.venue_specialists = temp;

                this.loadSettings();
                this.loading = false;
            },
            async loadIndustries() {
                try {
                    const response = await CompanyService.getIndustries();
                    this.industries = response.data || [];
                } catch (error) {
                    console.error('Failed to load industries:', error);
                    this.industries = [];
                }
            },
            async loadSettings() {
                let settings = await LeadService.getVenueSpecialistSetting();
                this.distribute_leads_evenly = (this.country).toLowerCase() == "uk" ? settings.data.distribute_leads_evenly_uk : settings.data.distribute_leads_evenly_au;
            },
            async updateField(id, field, value) {
                this.loading = true;
                let data = await LeadService.changeVenueSpecialistAttribute(id, field, value);
                this.loadPage();
                this.loading = false;
            },
            async changeDistributionStyle() {
                this.loading = true;;
                let attribute = 'distribute_leads_evenly_' + (this.country).toLowerCase();
                let data = await LeadService.changeVenueSpecialistSetting(attribute, this.distribute_leads_evenly);
                this.loadSettings();
                this.loading = false;
            },
        },
        watch: {
            showPauseUntilDatePicker: function(newVal) {
                if (newVal) {
                    if (this.pausedUpto(this.selectedVenueRep)) {
                        this.wasAPauseTillDateSelected = true;
                        return;
                    }
                }

                this.wasAPauseTillDateSelected = false;
            }
        }
    };
</script>
<style lang="scss">
    .no-border-card .card-footer {
        border-top: 0;
    }

    .el-table .cell {
        word-break: normal;
    }

    .el-table--enable-row-hover .el-table__body tr:hover>td {
        background-color: #f6f9fc !important;
        cursor: pointer;
    }

    .inline-input {
        width: 120px;
        display: inline-block;
    }

    #active_toggle {
        display: inline-block;
    }

    .el-table__body .event-type-cell .cell {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 100%;
        box-sizing: border-box;

        .custom-control {
            padding-left: 0;
            min-height: 1rem;
            width: 1rem;
            height: 1rem;
            position: relative;
            display: inline-block;
        }

        .custom-control-label {
            position: static;

            &::before,
            &::after {
                top: 0;
                left: 0;
                right: 0;
                margin: 0 auto;
            }
        }
    }

    .el-table__body .lead-cap-cell {
        text-align: left;

        .cell {
            display: inline-flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 4px;
        }
    }

    .el-table__body .paused-attributes {
        text-align: left;

        .cell {
            display: inline-flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 4px;
        }

        .cell > div {
            display: inline-flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 4px;
        }

        .paused-date-btn {
            margin-left: 0;
        }
    }

    .active-days-trigger {
        min-width: 110px;
        text-align: left;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .active-days-popover {
        .active-days-group {
            display: flex;
            flex-wrap: nowrap;
            gap: 4px;
        }

        .active-day-btn {
            width: 32px;
            height: 32px;
            padding: 0;
            font-size: 12px;
            font-weight: 600;
            line-height: 1;
            border-radius: 50%;
            display: inline-flex;
            align-items: center;
            justify-content: center;
        }
    }
</style>